import { validateEngineInput } from "../contracts/validate";
import type { EngineInput, EngineOutput, Finding, RunStatus } from "../contracts/types";
import { SCHEMA_VERSION } from "../contracts/types";
import { parseOpenApiDocument } from "./openapi-parse";
import type { ResolvedSecurity } from "./openapi-parse";
import { generateFailureCases } from "./failure-rules";
import type { FailureCase } from "./failure-rules";
import { buildTestExample } from "./test-example";
import type { TestExample } from "./test-example";
import { computeCoverage } from "./coverage";
import type { CoverageSummary } from "./coverage";
import { LIMITS } from "./limits";
import { fingerprintPayload, makeRunId } from "./ids";

export const RULES_VERSION = "1.0.0";

export interface OperationMatrix {
  operationId: string;
  method: string;
  path: string;
  summary: string;
  security: ResolvedSecurity[];
  cases: FailureCase[];
  testExamples: TestExample[];
}

export interface GenerateMatrixResult {
  output: EngineOutput;
  matrix: OperationMatrix[];
  coverage: CoverageSummary;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractScenarioId(rawInput: unknown): string {
  if (isPlainObject(rawInput) && typeof rawInput.scenarioId === "string") return rawInput.scenarioId;
  return "unknown";
}

function truncateFindings(findings: Finding[]): Finding[] {
  if (findings.length <= LIMITS.maxFindings) return findings;
  const truncated = findings.slice(0, LIMITS.maxFindings - 1);
  truncated.push({
    ruleId: "FINDINGS_TRUNCATED",
    severity: "warning",
    message: `Se truncó la lista de hallazgos a ${LIMITS.maxFindings - 1} de ${findings.length} totales.`,
  });
  return truncated;
}

function failedResult(scenarioId: string, findings: Finding[], summary: string): GenerateMatrixResult {
  const runId = makeRunId(scenarioId, fingerprintPayload(scenarioId));
  return {
    output: {
      schemaVersion: SCHEMA_VERSION,
      runId,
      status: "failed",
      summary,
      findings: truncateFindings(findings),
      evidence: { rulesVersion: RULES_VERSION, scenarioId },
    },
    matrix: [],
    coverage: { perOperation: [], overall: { relevant: 0, covered: 0, percentage: 100 } },
  };
}

/**
 * Punto de entrada puro del motor de dominio. Recibe la envolvente de entrada
 * (contracts/input.schema.json), valida, parsea el documento OpenAPI y genera
 * la matriz de casos de fallo 4xx/5xx por operación, junto a la medición de
 * cobertura negativa. Nunca lanza excepciones: cualquier problema se traduce
 * en un EngineOutput con status "failed" y findings tipados.
 *
 * `generateMatrix` es la interfaz de dominio descrita en
 * 07-contratos-interfaces.md.
 */
export function generateMatrix(rawInput: unknown, options: { shouldCancel?: () => boolean } = {}): GenerateMatrixResult {
  const scenarioId = extractScenarioId(rawInput);
  const validation = validateEngineInput(rawInput);
  if (!validation.valid) {
    const findings: Finding[] = validation.errors.map((error) => ({
      ruleId: error.code,
      severity: "critical",
      message: error.message,
      evidencePath: error.path,
    }));
    return failedResult(scenarioId, findings, "La entrada no cumple el contrato input.schema.json.");
  }

  const input = rawInput as EngineInput;
  const findings: Finding[] = [];

  if (!input.options.deterministic) {
    findings.push({
      ruleId: "ADAPTER_DISABLED",
      severity: "info",
      message:
        "El adaptador real está deshabilitado por diseño (kill switch permanente en este MVP). Se usó el motor determinista local sin llamadas de red.",
    });
  }

  const parseResult = parseOpenApiDocument(input.payload);

  if (parseResult.errors.length > 0) {
    for (const error of parseResult.errors) {
      findings.push({ ruleId: "INPUT_INVALID", severity: "critical", message: error, evidencePath: "#/payload" });
    }
    return failedResult(input.scenarioId, findings, "El documento OpenAPI no se pudo interpretar de forma segura.");
  }

  for (const warning of parseResult.warnings) {
    findings.push({ ruleId: "DOCUMENT_WARNING", severity: "warning", message: warning, evidencePath: "#/payload" });
  }

  const matrix: OperationMatrix[] = [];
  const casesByOperation = new Map<string, FailureCase[]>();
  let cancelled = false;

  for (const operation of parseResult.operations) {
    if (options.shouldCancel?.()) {
      cancelled = true;
      break;
    }
    const cases = generateFailureCases(operation);
    const testExamples = cases.map((failureCase) => buildTestExample(operation, failureCase));
    casesByOperation.set(operation.operationId, cases);
    matrix.push({
      operationId: operation.operationId,
      method: operation.method,
      path: operation.path,
      summary: operation.summary,
      security: operation.security,
      cases,
      testExamples,
    });
  }

  if (cancelled) {
    const runId = makeRunId(input.scenarioId, fingerprintPayload(input.payload));
    return {
      output: {
        schemaVersion: SCHEMA_VERSION,
        runId,
        status: "cancelled",
        summary: "El visitante canceló la ejecución antes de completarla.",
        findings: truncateFindings([
          ...findings,
          { ruleId: "RUN_CANCELLED", severity: "info", message: "Ejecución cancelada por el visitante." },
        ]),
        evidence: { rulesVersion: RULES_VERSION, scenarioId: input.scenarioId },
      },
      matrix,
      coverage: computeCoverage(casesByOperation),
    };
  }

  const coverage = computeCoverage(casesByOperation);
  const totalCases = matrix.reduce((sum, operation) => sum + operation.cases.length, 0);

  if (matrix.length === 0) {
    findings.push({
      ruleId: "NO_OPERATIONS",
      severity: "warning",
      message: "El documento no contiene operaciones reconocibles (paths + método HTTP).",
      evidencePath: "#/payload/paths",
    });
  }

  const status: RunStatus = matrix.length === 0 ? "partial" : "completed";
  const summary =
    matrix.length === 0
      ? "Ejecución completada sin operaciones que analizar."
      : `Se generaron ${totalCases} casos de fallo sobre ${matrix.length} operación(es). Cobertura negativa revisada: ${coverage.overall.percentage}%.`;

  const runId = makeRunId(input.scenarioId, fingerprintPayload(input.payload));

  return {
    output: {
      schemaVersion: SCHEMA_VERSION,
      runId,
      status,
      summary,
      findings: truncateFindings(findings),
      evidence: { rulesVersion: RULES_VERSION, scenarioId: input.scenarioId },
    },
    matrix,
    coverage,
  };
}

/**
 * Aplica una edición/justificación manual (P8-R3) a un caso concreto de la
 * matriz, de forma inmutable, y recalcula la cobertura negativa (P8-R4).
 */
export function overrideCase(
  matrix: OperationMatrix[],
  caseId: string,
  patch: Partial<Pick<FailureCase, "justification" | "accepted" | "reviewed" | "severity">>,
): { matrix: OperationMatrix[]; coverage: CoverageSummary } {
  const nextMatrix = matrix.map((operation) => ({
    ...operation,
    cases: operation.cases.map((failureCase) => (failureCase.id === caseId ? { ...failureCase, ...patch } : failureCase)),
  }));
  const casesByOperation = new Map(nextMatrix.map((operation) => [operation.operationId, operation.cases]));
  return { matrix: nextMatrix, coverage: computeCoverage(casesByOperation) };
}
